import clsx from 'clsx'
import {FC} from 'react'
import {useMetric} from '../../core/MetricProvider'
import {MetricDistributionItem} from '../../core/_models'
import {formatMoney, formatNumber, formatPercent} from '../../core/_format'
import {useTableSort} from '../../core/useTableSort'

type DistributionProps = {
  title: string
  label: string
  items: MetricDistributionItem[]
}

const DistributionTable: FC<DistributionProps> = ({title, label, items}) => {
  const {rows, toggleSort, sortClass} = useTableSort(items)

  const columns: Array<{key: keyof MetricDistributionItem; title: string; className?: string}> = [
    {key: 'label', title: label},
    {key: 'clients', title: 'Clientes', className: 'text-end'},
    {key: 'percent', title: '%', className: 'text-end'},
    {key: 'amount', title: 'Valor vendido', className: 'text-end'},
  ]

  return (
    <div className='col-12 col-xl-6 mb-8'>
      <h4 className='fw-bolder text-gray-800 mb-4'>{title}</h4>
      <div className='table-responsive'>
        <table className='table align-middle table-row-dashed fs-6 gy-3'>
          <thead>
            <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={clsx(column.className, sortClass(column.key))}
                  style={{cursor: 'pointer'}}
                  onClick={() => toggleSort(column.key)}
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='text-gray-600 fw-bold'>
            {rows.map((item) => (
              <tr key={item.key}>
                <td>{item.label}</td>
                <td className='text-end'>{formatNumber(item.clients)}</td>
                <td className='text-end'>{formatPercent(item.percent)}</td>
                <td className='text-end'>{formatMoney(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const Indicator: FC<{label: string; value: string}> = ({label, value}) => (
  <div className='col-6 col-md-4 col-xl mb-5'>
    <div className='border border-gray-300 border-dashed rounded py-4 px-5 h-100'>
      <div className='fs-2 fw-bolder text-gray-800'>{value}</div>
      <div className='fw-bold fs-7 text-gray-500'>{label}</div>
    </div>
  </div>
)

const SummaryTab = () => {
  const {summary} = useMetric()

  if (!summary) {
    return null
  }

  const {indicators, distributions} = summary

  return (
    <>
      <div className='row'>
        <Indicator label='Total de clientes' value={formatNumber(indicators.totalClients)} />
        <Indicator label='Clientes ativos' value={formatNumber(indicators.activeClients)} />
        <Indicator label='Clientes suspensos' value={formatNumber(indicators.suspendedClients)} />
        <Indicator label='Clientes restritos' value={formatNumber(indicators.restrictedClients)} />
        <Indicator label='Clientes com distrato' value={formatNumber(indicators.distractClients)} />
      </div>
      <div className='row mb-5'>
        <Indicator label='Clientes com compra' value={formatNumber(indicators.buyers)} />
        <Indicator label='Quantidade de vendas' value={formatNumber(indicators.salesCount)} />
        <Indicator label='Valor total vendido' value={formatMoney(indicators.totalAmount)} />
        <Indicator label='Ticket médio (por venda)' value={formatMoney(indicators.averageTicket)} />
        <Indicator
          label='Média de sessões por cliente (com compra)'
          value={formatNumber(indicators.averageSessions, 2)}
        />
      </div>

      <div className='row'>
        <DistributionTable title='Distribuição por sexo' label='Sexo' items={distributions.gender} />
        <DistributionTable
          title='Distribuição por faixa de compra (R$)'
          label='Faixa'
          items={distributions.purchaseRange}
        />
        <DistributionTable
          title='Distribuição por tempo desde a última compra'
          label='Tempo'
          items={distributions.lastPurchaseRange}
        />
        <DistributionTable title='Distribuição por origem' label='Origem' items={distributions.origin} />
      </div>
    </>
  )
}

export {SummaryTab}
